/* eslint-disable max-lines */
import React, {
 useState, useEffect, useCallback, useContext
} from 'react';
import {
 useParams
} from 'react-router-dom';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from 'shared/components/ui/dialog';
import {
 Button
} from 'shared/components/ui/button';
import {
 Input
} from 'shared/components/ui/input';
import {
 Loader2, FileText
} from 'lucide-react';
import {
 Skeleton
} from 'shared/components/ui/skeleton';
import {
 connectionService
} from 'shared/api/services/BEX';
import {
 bexSchemaService
} from 'shared/api/services/BEX';
import {
 SchemaColumnDataModel
} from 'shared/api/services/BEXO/Types/Bexo.types';
import {
 Context
} from 'shared/context/GlobalState';
import {
 Connection
} from 'builders/builderApp/Connection/Components/Types/ConnectionTypes';
import {
 ConnectionForm
} from 'builders/builderApp/Connection/ConnectionForm';
import {
 useDataBuilderV2
} from '../DataBuilderV2Context';
import {
 SchemaListItem
} from '../types/schema.types';
import {
 ConnectionTestState
} from '../components/ConnectionsTable';
import ConnectionTree from '../components/ConnectionTree';
import SchemaDetailPanel from '../components/connection-explorer/SchemaDetailPanel';
import ConnectionMethodSelect from '../components/ConnectionMethodSelect';
import ImportSpecUpload from '../components/ImportSpecUpload';
import AIDescribeConnection from '../components/AIDescribeConnection';
import SchemaMethodSelect from '../components/SchemaMethodSelect';
import AIGenerateSchema from '../components/AIGenerateSchema';
import ImportFromDataSource from '../components/ImportFromDataSource';
import SchemaEditor from '../components/SchemaEditor';
import {
 ParsedSpec
} from '../utils/specParser';

type OverlayView =
    | null
    | 'connection-method-select'
    | 'connection-import-spec'
    | 'connection-ai-describe'
    | 'connection-manual-form'
    | 'schema-method-select'
    | 'schema-ai-generate'
    | 'schema-import-source'
    | 'schema-editor';

const ConnectionsPage: React.FC = () => {
    const {
 appId, oragID
} = useParams<{ appId: string; oragID: string }>();
    const {
 addNewNotifcation
} = useContext(Context);
    const {
 refreshStats
} = useDataBuilderV2();

    // Data state
    const [connections, setConnections] = useState<Connection[]>([]);
    const [schemas, setSchemas] = useState<SchemaListItem[]>([]);
    const [isLoadingConnections, setIsLoadingConnections] = useState(true);
    const [isLoadingSchemas, setIsLoadingSchemas] = useState(true);
    const [testStatuses, setTestStatuses] = useState<Record<string, ConnectionTestState>>({});

    // Selection state
    const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(null);
    const [selectedSchemaName, setSelectedSchemaName] = useState<string | null>(null);

    // Overlay / form state
    const [overlayView, setOverlayView] = useState<OverlayView>(null);
    const [editingConnection, setEditingConnection] = useState<Connection | null>(null);
    const [editingSchema, setEditingSchema] = useState<SchemaListItem | null>(null);

    // Connection form pre-fill
    const [prefillData, setPrefillData] = useState<Partial<Connection> | null>(null);
    const [successBanner, setSuccessBanner] = useState<string | null>(null);

    // Schema editor pre-fill
    const [prefillTableName, setPrefillTableName] = useState<string | null>(null);
    const [prefillColumns, setPrefillColumns] = useState<SchemaColumnDataModel[] | null>(null);

    // Schema generation modal state
    const [showSchemaModal, setShowSchemaModal] = useState(false);
    const [schemaModalData, setSchemaModalData] = useState<{ endpointCount: number; fileName: string } | null>(null);

    // Delete confirmation state
    const [deleteConnectionTarget, setDeleteConnectionTarget] = useState<Connection | null>(null);
    const [deleteConnectionText, setDeleteConnectionText] = useState('');
    const [deleteConnectionLoading, setDeleteConnectionLoading] = useState(false);

    const [deleteSchemaTarget, setDeleteSchemaTarget] = useState<SchemaListItem | null>(null);
    const [deleteSchemaText, setDeleteSchemaText] = useState('');
    const [deleteSchemaLoading, setDeleteSchemaLoading] = useState(false);

    // ─── Data Fetching ───────────────────────────────────────────

    const fetchConnections = useCallback(async () => {
        if (!oragID || !appId) return;
        setIsLoadingConnections(true);
        try {
            const data = await connectionService.getConnections(oragID, appId);
            if (Array.isArray(data)) {
                const mapped: Connection[] = data.map((item: any) => ({
                    connection_id: item.connection_id,
                    connection_name: item.connection_name,
                    description: item.description || '',
                    source_type: item.source_type || '',
                    settings: item.settings || {},
                    orgId: item.org_id || oragID,
                    appId: item.app_id || appId,
                    name: item.connection_name,
                    advancedOptions: item.advanced_options || {},
                    data: item.data || {},
                    updated_at: item.updated_at || null,
                }));
                setConnections(mapped);
            }
        } catch (error) {
            console.error('Error fetching connections:', error);
        } finally {
            setIsLoadingConnections(false);
        }
    }, [oragID, appId]);

    const parseRawSchemas = (rawSchemas: any[], connectionId?: string): SchemaListItem[] => {
        if (!Array.isArray(rawSchemas)) return [];
        const schemaMap = new Map<string, { columns: SchemaColumnDataModel[]; connectionId?: string }>();
        rawSchemas.forEach((item: any) => {
            const name = item.tableName || item.table_name;
            if (!name) return;
            if (!schemaMap.has(name)) {
                schemaMap.set(name, {
                    columns: [],
                    connectionId: connectionId || item.connectionId || item.connection_id,
                });
            }
            const entry = schemaMap.get(name)!;
            if (Array.isArray(item.columns)) {
                entry.columns = item.columns;
            } else if (item.columnName) {
                entry.columns.push(item);
            }
            if (connectionId || item.connectionId || item.connection_id) {
                entry.connectionId = connectionId || item.connectionId || item.connection_id;
            }
        });

        return Array.from(schemaMap.entries()).map(([tableName, data]) => ({
            tableName,
            columns: data.columns,
            fieldCount: data.columns.length,
            relationshipCount: data.columns.filter((c) => c.constraints?.includes('FOREIGN KEY')).length,
            calculationCount: data.columns.filter((c) => c.dataType === 'equation').length,
            connectionId: data.connectionId,
            updatedAt: undefined,
        }));
    };

    const fetchSchemas = useCallback(async () => {
        if (!oragID || !appId) return;
        setIsLoadingSchemas(true);
        try {
            const res = await bexSchemaService.retrieveSchemas('*', appId, oragID);
            const rawSchemas = res.data?.schemas || res.data || [];
            setSchemas(parseRawSchemas(rawSchemas));
        } catch (error) {
            console.error('Error fetching schemas:', error);
        } finally {
            setIsLoadingSchemas(false);
        }
    }, [oragID, appId]);

    const fetchSchemasForConnection = useCallback(async (connectionId: string) => {
        if (!oragID || !appId) return;
        try {
            const res = await bexSchemaService.retrieveSchemas('*', appId, oragID, connectionId);
            const rawSchemas = res.data?.schemas || res.data || [];
            const connectionSchemas = parseRawSchemas(rawSchemas, connectionId);

            setSchemas((prev) => {
                // Remove old schemas for this connection, then add fresh ones
                const withoutConnection = prev.filter((s) => s.connectionId !== connectionId);
                return [...withoutConnection, ...connectionSchemas];
            });
        } catch (error) {
            console.error(`Error fetching schemas for connection ${connectionId}:`, error);
        }
    }, [oragID, appId]);

    useEffect(() => {
        fetchConnections();
        fetchSchemas();
    }, [fetchConnections, fetchSchemas]);

    // ─── Connection Actions ──────────────────────────────────────

    const handleTestConnection = async (connection: Connection) => {
        setTestStatuses((prev) => ({
            ...prev,
            [connection.connection_id]: {
 status: 'testing',
message: '',
testedAt: null
},
        }));
        try {
            await connectionService.testSingleConnection(
                connection.orgId, connection.appId, connection.connection_id
            );
            setTestStatuses((prev) => ({
                ...prev,
                [connection.connection_id]: {
                    status: 'success',
                    message: 'Connection is healthy',
                    testedAt: new Date(),
                },
            }));
            addNewNotifcation('Connection test passed', 'success');
        } catch (error: any) {
            const msg = error?.response?.data?.detail || error?.message || 'Connection test failed';
            setTestStatuses((prev) => ({
                ...prev,
                [connection.connection_id]: {
 status: 'error',
message: msg,
testedAt: new Date()
},
            }));
            addNewNotifcation('Connection test failed', 'danger');
        }
    };

    const handleDeleteConnection = async () => {
        if (!deleteConnectionTarget || !oragID || !appId) return;
        setDeleteConnectionLoading(true);
        try {
            await connectionService.deleteConnection(oragID, appId, deleteConnectionTarget.connection_id);
            setConnections((prev) => prev.filter((c) => c.connection_id !== deleteConnectionTarget.connection_id));
            if (selectedConnectionId === deleteConnectionTarget.connection_id) {
                setSelectedConnectionId(null);
                setSelectedSchemaName(null);
            }
            addNewNotifcation('Connection deleted successfully', 'success');
            setDeleteConnectionTarget(null);
            setDeleteConnectionText('');
            await refreshStats();
        } catch (error) {
            console.error('Delete failed:', error);
            addNewNotifcation('Failed to delete connection', 'danger');
        } finally {
            setDeleteConnectionLoading(false);
        }
    };

    const handleConnectionSaved = async () => {
        await fetchConnections();
        await refreshStats();
        setOverlayView(null);
        setEditingConnection(null);
        setPrefillData(null);
        setSuccessBanner(null);
    };

    const handleEditConnection = (connection: Connection) => {
        setEditingConnection(connection);
        setPrefillData(null);
        setSuccessBanner(null);
        setOverlayView('connection-manual-form');
    };

    const handleNewConnection = () => {
        setEditingConnection(null);
        setPrefillData(null);
        setSuccessBanner(null);
        setOverlayView('connection-method-select');
    };

    // ─── Connection Import / AI ──────────────────────────────────

    const convertParsedSpecToPrefill = (parsed: ParsedSpec): Partial<Connection> => {
        return {
            connection_name: parsed.connectionName,
            description: parsed.description,
            source_type: parsed.sourceType,
            settings: {
                ...parsed.settings,
                base_url: parsed.baseUrl || parsed.settings?.base_url,
                auth_type: parsed.authType !== 'none' ? parsed.authType : undefined,
                ...parsed.authDetails,
            },
        } as Partial<Connection>;
    };

    const handleSpecParsed = (parsed: ParsedSpec) => {
        const prefill = convertParsedSpecToPrefill(parsed);
        setPrefillData(prefill);
        setSuccessBanner(`Success! We extracted the connection details from '${parsed.fileName}'.`);

        if (parsed.endpointCount > 0) {
            setSchemaModalData({
 endpointCount: parsed.endpointCount,
fileName: parsed.fileName
});
            setShowSchemaModal(true);
        } else {
            setOverlayView('connection-manual-form');
        }
    };

    const handleAIConnectionResult = (parsed: ParsedSpec) => {
        const prefill = convertParsedSpecToPrefill(parsed);
        setPrefillData(prefill);
        setSuccessBanner('AI has configured your connection settings. Review and save when ready.');
        setOverlayView('connection-manual-form');
    };

    const handleSchemaModalClose = (generate: boolean) => {
        setShowSchemaModal(false);
        setSchemaModalData(null);
        if (generate) {
            addNewNotifcation('Schema generation started. You can continue setting up your connection.', 'success');
        }
        setOverlayView('connection-manual-form');
    };

    // ─── Schema Actions ──────────────────────────────────────────

    const handleDeleteSchema = async () => {
        if (!deleteSchemaTarget || !oragID || !appId) return;
        setDeleteSchemaLoading(true);
        try {
            const payload = {
                schemas: [{
                    tableName: deleteSchemaTarget.tableName,
                    columns: deleteSchemaTarget.columns.map((c) => ({
 columnName: c.columnName
})),
                }],
            };
            await bexSchemaService.deleteSchemas(payload, oragID, appId, deleteSchemaTarget.connectionId || undefined);
            setSchemas((prev) => prev.filter((s) => s.tableName !== deleteSchemaTarget.tableName));
            if (selectedSchemaName === deleteSchemaTarget.tableName) {
                setSelectedSchemaName(null);
            }
            addNewNotifcation('Schema deleted successfully', 'success');
            setDeleteSchemaTarget(null);
            setDeleteSchemaText('');
            await refreshStats();
        } catch (error) {
            console.error('Delete failed:', error);
            addNewNotifcation('Failed to delete schema', 'danger');
        } finally {
            setDeleteSchemaLoading(false);
        }
    };

    const handleSchemaSaved = async () => {
        await fetchSchemas();
        await refreshStats();
        setOverlayView(null);
        setEditingSchema(null);
        setPrefillTableName(null);
        setPrefillColumns(null);
    };

    const handleNewSchema = (connectionId?: string | null) => {
        setEditingSchema(null);
        if (connectionId) setSelectedConnectionId(connectionId);
        setPrefillTableName(null);
        setPrefillColumns(null);
        setOverlayView('schema-method-select');
    };

    const handleEditSchema = (schema: SchemaListItem) => {
        setEditingSchema(schema);
        setPrefillTableName(null);
        setPrefillColumns(null);
        setOverlayView('schema-editor');
    };

    const handleAISchemaResult = (tableName: string, columns: SchemaColumnDataModel[]) => {
        setPrefillTableName(tableName);
        setPrefillColumns(columns);
        setOverlayView('schema-editor');
    };

    const handleImportSchemaResult = (tableName: string, columns: SchemaColumnDataModel[]) => {
        setPrefillTableName(tableName);
        setPrefillColumns(columns);
        setOverlayView('schema-editor');
    };

    const handleSchemaEditorCancel = () => {
        setOverlayView(null);
        setEditingSchema(null);
        setPrefillTableName(null);
        setPrefillColumns(null);
    };

    // ─── Selection ───────────────────────────────────────────────

    const handleSelectConnection = (connectionId: string) => {
        setSelectedConnectionId(connectionId);
        setSelectedSchemaName(null);
    };

    const handleSelectSchema = (schemaName: string, connectionId: string | null) => {
        setSelectedSchemaName(schemaName);
        if (connectionId) setSelectedConnectionId(connectionId);
    };

    // ─── Derived Data ────────────────────────────────────────────

    const selectedSchema = schemas.find((s) => s.tableName === selectedSchemaName) || null;
    const selectedConnection = connections.find((c) => c.connection_id === selectedConnectionId) || null;

    // ─── Overlay Rendering ───────────────────────────────────────

    if (overlayView) {
        switch (overlayView) {
            case 'connection-method-select':
                return (
                    <ConnectionMethodSelect
                        onSelectManual={() => setOverlayView('connection-manual-form')}
                        onSelectImport={() => setOverlayView('connection-import-spec')}
                        onSelectAI={() => setOverlayView('connection-ai-describe')}
                        onBack={() => setOverlayView(null)}
                    />
                );
            case 'connection-import-spec':
                return (
                    <ImportSpecUpload
                        onBack={() => setOverlayView('connection-method-select')}
                        onSpecParsed={handleSpecParsed}
                    />
                );
            case 'connection-ai-describe':
                return (
                    <AIDescribeConnection
                        onBack={() => setOverlayView('connection-method-select')}
                        onAIResult={handleAIConnectionResult}
                    />
                );
            case 'connection-manual-form':
                return (
                    <ConnectionForm
                        onConnectionSaved={handleConnectionSaved}
                        onCancel={() => { setOverlayView(null); setEditingConnection(null); setPrefillData(null); setSuccessBanner(null); }}
                        editingConnection={editingConnection || undefined}
                        initialValues={prefillData || undefined}
                        successBanner={successBanner || undefined}
                    />
                );
            case 'schema-method-select':
                return (
                    <SchemaMethodSelect
                        onSelectManual={() => setOverlayView('schema-editor')}
                        onSelectImport={() => setOverlayView('schema-import-source')}
                        onSelectAI={() => setOverlayView('schema-ai-generate')}
                        onBack={() => setOverlayView(null)}
                    />
                );
            case 'schema-ai-generate':
                return (
                    <AIGenerateSchema
                        onBack={() => setOverlayView('schema-method-select')}
                        onResult={handleAISchemaResult}
                    />
                );
            case 'schema-import-source':
                return (
                    <ImportFromDataSource
                        onBack={() => setOverlayView('schema-method-select')}
                        onResult={handleImportSchemaResult}
                    />
                );
            case 'schema-editor':
                return (
                    <SchemaEditor
                        schema={editingSchema}
                        initialTableName={prefillTableName || undefined}
                        initialColumns={prefillColumns || undefined}
                        initialConnectionId={selectedConnectionId || undefined}
                        onSave={handleSchemaSaved}
                        onCancel={handleSchemaEditorCancel}
                    />
                );
        }
    }

    // ─── Main Explorer Layout ────────────────────────────────────

    if (isLoadingConnections || isLoadingSchemas) {
        return (
            <div className="flex h-[calc(100vh-8rem)] -m-4">
                {/* Left Tree Skeleton */}
                <div className="w-[280px] shrink-0 border-r p-3 space-y-4">
                    <div className="flex items-center justify-between">
                        <Skeleton className="h-5 w-16" />
                        <Skeleton className="h-7 w-7 rounded" />
                    </div>
                    <Skeleton className="h-8 w-full" />
                    <div className="space-y-2 pt-2">
                        {Array.from({
 length: 4
}).map((_, i) => (
    <div key={i} className="space-y-1.5">
        <div className="flex items-center gap-2 px-1">
            <Skeleton className="h-3.5 w-3.5" />
            <Skeleton className="h-2 w-2 rounded-full" />
            <Skeleton className="h-4 w-32" />
        </div>
        {i < 2 && (
        <div className="ml-8 space-y-1">
            <Skeleton className="h-3.5 w-24" />
            <Skeleton className="h-3.5 w-20" />
        </div>
                                )}
    </div>
                        ))}
                    </div>
                </div>
                {/* Right Panel Skeleton */}
                <div className="flex-1 border-l flex flex-col items-center justify-center gap-4">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">Loading connections and schemas...</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="flex h-[calc(100vh-8rem)] -m-4">
                {/* Left Tree Panel */}
                <div className="w-[280px] shrink-0">
                    <ConnectionTree
                        connections={connections}
                        schemas={schemas}
                        testStatuses={testStatuses}
                        selectedConnectionId={selectedConnectionId}
                        selectedSchemaName={selectedSchemaName}
                        onSelectConnection={handleSelectConnection}
                        onSelectSchema={handleSelectSchema}
                        onNewConnection={handleNewConnection}
                        onNewSchema={(connId) => handleNewSchema(connId)}
                        onEditConnection={handleEditConnection}
                        onDeleteConnection={(conn) => setDeleteConnectionTarget(conn)}
                        onTestConnection={handleTestConnection}
                        onEditSchema={handleEditSchema}
                        onDeleteSchema={(schema) => setDeleteSchemaTarget(schema)}
                        onExpandConnection={fetchSchemasForConnection}
                    />
                </div>

                {/* Right Detail Panel */}
                <div className="flex-1 border-l min-w-0">
                    <SchemaDetailPanel
                        schema={selectedSchema}
                        connectionName={selectedConnection?.connection_name || null}
                        onSchemaSaved={async () => { await fetchSchemas(); await refreshStats(); }}
                    />
                </div>
            </div>

            {/* Delete Connection Dialog */}
            <Dialog
                open={!!deleteConnectionTarget}
                onOpenChange={(open) => {
                    if (!open) { setDeleteConnectionTarget(null); setDeleteConnectionText(''); }
                }}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Connection</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete the <span className="font-semibold">"{deleteConnectionTarget?.connection_name}"</span> connection?
                            This action cannot be undone and may break schemas or workflows that depend on it.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-2">
                        <p className="text-sm text-muted-foreground mb-2">
                            Type <span className="font-semibold">{deleteConnectionTarget?.connection_name}</span> to confirm:
                        </p>
                        <Input
                            placeholder="Type connection name to confirm"
                            value={deleteConnectionText}
                            onChange={(e) => setDeleteConnectionText(e.target.value)}
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setDeleteConnectionTarget(null); setDeleteConnectionText(''); }}>
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            disabled={deleteConnectionText !== deleteConnectionTarget?.connection_name || deleteConnectionLoading}
                            onClick={handleDeleteConnection}
                        >
                            {deleteConnectionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Schema Dialog */}
            <Dialog
                open={!!deleteSchemaTarget}
                onOpenChange={(open) => {
                    if (!open) { setDeleteSchemaTarget(null); setDeleteSchemaText(''); }
                }}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Schema</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete the <span className="font-semibold">"{deleteSchemaTarget?.tableName}"</span> schema?
                            This action cannot be undone and will remove all fields and relationships.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-2">
                        <p className="text-sm text-muted-foreground mb-2">
                            Type <span className="font-semibold">{deleteSchemaTarget?.tableName}</span> to confirm:
                        </p>
                        <Input
                            placeholder="Type schema name to confirm"
                            value={deleteSchemaText}
                            onChange={(e) => setDeleteSchemaText(e.target.value)}
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setDeleteSchemaTarget(null); setDeleteSchemaText(''); }}>
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            disabled={deleteSchemaText !== deleteSchemaTarget?.tableName || deleteSchemaLoading}
                            onClick={handleDeleteSchema}
                        >
                            {deleteSchemaLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Schema generation modal (shown after spec import) */}
            <Dialog open={showSchemaModal} onOpenChange={(open) => { if (!open) handleSchemaModalClose(false); }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileText className="h-5 w-5" />
                            Generate Schemas?
                        </DialogTitle>
                        <DialogDescription>
                            We detected <span className="font-semibold">{schemaModalData?.endpointCount}</span> endpoints
                            in '<span className="font-semibold">{schemaModalData?.fileName}</span>'.
                            Would you like us to auto-generate schemas from this specification?
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => handleSchemaModalClose(false)}>
                            No, I'll do it later
                        </Button>
                        <Button onClick={() => handleSchemaModalClose(true)}>
                            Yes, Generate
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ConnectionsPage;
